import { jSith } from "../jquery-replacement";
import { type StateI } from "../../pojo/StateI";

export namespace S25DirectiveUtil {
    export function preCompileStr(markup: string): any {
        return window.angBridge.$compile(window.angBridge.element(markup));
    }

    export function preCompileTmpl(tmplId: string): any {
        return preCompileStr(window.angBridge.$templateCache.get(tmplId));
    }

    export function compileTmpl(tmplId: string, scope: any): any {
        return preCompileTmpl(tmplId)(scope);
    }

    export function compile(element: any, scope: any): any {
        return window.angBridge.$compile(window.angBridge.element(element))(scope);
    }

    export function applyPreCompiledFn(tmplId: string, scope: any, cloneAttachFn: any) {
        const templateFn: any = window.angBridge.$templateCache.get(tmplId);
        templateFn && templateFn(scope, cloneAttachFn);
    }

    export function appendPreCompiled(tmplId: string, scope: any, element: any): any {
        applyPreCompiledFn(tmplId, scope, function (clonedElement: any) {
            // jSith.append(element, clonedElement);
            element.append(clonedElement);
        });
    }

    export function createApiFnRetFn(event: any): Function {
        return function (contextScope: any, object: any) {
            contextScope && contextScope.$broadcast(event, object);
        };
    }

    export function createApiFn(name: string, event: any): any {
        const result: any = {};
        // 20161107, Mikhail cannot wrap all createApiFn into $timeout because
        // callers might call $scope.destroy() before we fire
        // and thus we won't be able to destroy child appended to body (qtip)
        result[name] = createApiFnRetFn(event);
        return result;
    }

    export function createApiDigestedFn(name: string, event: any): any {
        // 20161107, Mikhail - wrap into $timeout and $scope.digest,
        // so callers don't have to wrap to make sure directive initialized before calling API (i.e. $scope.$broadcast)
        const result: any = {};
        result[name] = function (contextScope: any, object: any) {
            timeoutDigest(function () {
                createApiFnRetFn(event)(contextScope, object);
            }, contextScope);
        };
        return result;
    }

    export function createUpdateFn(event: any): any {
        return createApiFn("update", event);
    }

    export function createRefreshFn(event: any): any {
        return createApiFn("refresh", event);
    }

    export function createReloadFn(event: any): any {
        return createApiFn("reload", event);
    }

    export function createResetFn(event: any): any {
        return createApiFn("reset", event);
    }

    export function createInitFn(event: any): any {
        return createApiDigestedFn("init", event);
    }

    export function createDestroyFn(event: any): any {
        return createApiFn("destroy", event);
    }

    // 20160212 - Mikhail - wrap S25LoadingApi.destroy() into $timeout (and into scope.$digest() for consistency)
    // to keep 'loading' messages till Angular DOM Rendering is done, because Angular renders DOM in digest cycle
    // and $timeout ensures digest cycle is done.
    export function createDestroyYieldRenderingFn(event: any): any {
        return {
            destroyYieldRendering: function (contextScope: any, object: any) {
                // note: cannot call scope.$digest() before scope.$broadcast because occasionally ng-if is not executed if scope.$digest() was already called
                // thus cannot call createDigestFn() and using $timeout manually
                // 20160629, Mikhail - have to specify TRUE for $timeout
                // We have to include and rely on $timeout $rootScope.$digest()
                // because if $http data is empty (i.e. nothing to render) Angular does not run $digest()
                // and our $timeout never finishes
                // http://bugs.collegenet.com/jira/browse/ANG-217
                window.angBridge.$timeout(
                    createDigestFn(function () {
                        createDestroyFn(event).destroy(contextScope, object);
                    }, contextScope),
                    0,
                    true,
                );
            },
        };
    }

    export function createShowFn(event: any): any {
        return createApiFn("show", event);
    }

    export function createHideFn(event: any): any {
        return createApiFn("hide", event);
    }

    export function createSetFocusFn(event: any): any {
        return createApiDigestedFn("setFocus", event);
    }

    export function createDigestFn(fn: Function, scope: any): Function {
        return function () {
            // note: 20160426, Mikhail - Jasmine $timeoutMock does not run rootScope.$apply()
            // however, we have to run rootScope.$apply() (in addition to scope.$digest())
            // because there might be other scopes waiting for us (i.e. s25-directive-deferred)
            return window.angBridge.$timeout(
                function () {
                    scope && scope.$digest && scope.$digest();
                    return fn && fn();
                },
                0,
                true,
            );
        };
    }

    export function timeoutDigest(fn: Function, scope: any): any {
        return createDigestFn(fn, scope)();
    }

    export function digestPromise(fn: Function, scope: any): any {
        // return new Promise(function(resolve, reject) {
        return window.angBridge.$q(function (resolve: Function) {
            const result: any = fn && fn();
            timeoutDigest(function () {
                resolve(result);
            }, scope);
        });
    }

    export function createDeferredOnInitFn(scope: any): Function {
        return function () {
            // only run once
            if (!scope.deferredOnInit) {
                scope.deferredOnInit = {};
                // scope.deferredOnInit.promise = new Promise(function(resolve, reject) {
                scope.deferredOnInit.promise = window.angBridge.$q(function (resolve: Function) {
                    scope.deferredOnInit.resolve = resolve;
                });
            }
            return scope.deferredOnInit.promise;
        };
    }

    export function createStateInstance(active?: boolean): StateI {
        const state: StateI = {
            active: active || false,

            isActive() {
                return state.active;
            },

            init() {
                state.active = true;
            },

            destroy() {
                state.active = false;
            },
        };

        return state;
    }

    // ng-if is slow and creates flickering / flashing when switching from current displayed data into 'Loading' message
    // i.e. when opening Event Detail / Space Detail from outside link the previous data flashes before 'Loading' appears
    // this State immediately hides the element to avoid flickering
    export function createElementStateInstance($element: any): any {
        return {
            show() {
                jSith.removeClass($element, "hidden");
            },

            hide() {
                jSith.addClass($element, "hidden");
            },
        };
    }
}
